<script setup lang="ts">
/**
 * Inline player + waveform for an audio file entry in the site-files list.
 *
 * The component fetches the audio file as a Blob (via the same authenticated
 * file API used by `downloadFile`), creates an object URL for `<audio>`
 * playback and uses `decodeAudioData` to draw a downsampled waveform on a
 * canvas. Decoding failures gracefully degrade to a plain progress bar.
 */
interface AudioFile {
  id: string;
  name: string;
  path: string;
  type: string;
  length: number | null;
  metadata?: Record<string, any>;
}

const props = defineProps<{
  file: AudioFile;
  siteId: string;
  displayName?: string;
}>();

const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const peaks = ref<number[]>([]);
const waveformReady = ref(false);
const loadError = ref<string | null>(null);
const loading = ref(false);
const objectUrl = ref<string | null>(null);

const audioEl = ref<HTMLAudioElement | null>(null);
const waveformEl = ref<HTMLCanvasElement | null>(null);
const containerEl = ref<HTMLDivElement | null>(null);

const WAVE_BUCKETS = 96;

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function computePeaks(audioBuffer: AudioBuffer, buckets: number): number[] {
  const channelCount = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const samplesPerBucket = Math.max(1, Math.floor(length / buckets));
  const result: number[] = new Array(buckets).fill(0);

  for (let channel = 0; channel < channelCount; channel += 1) {
    const data = audioBuffer.getChannelData(channel);
    for (let bucket = 0; bucket < buckets; bucket += 1) {
      const start = bucket * samplesPerBucket;
      const end = Math.min(start + samplesPerBucket, length);
      let max = 0;
      for (let i = start; i < end; i += 1) {
        const v = Math.abs(data[i]);
        if (v > max) max = v;
      }
      if (max > result[bucket]) result[bucket] = max;
    }
  }

  const overall = result.reduce((m, v) => (v > m ? v : m), 0) || 1;
  return result.map((v) => v / overall);
}

function drawWaveform() {
  const canvas = waveformEl.value;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const cssWidth = canvas.clientWidth || 240;
  const cssHeight = canvas.clientHeight || 36;
  if (canvas.width !== cssWidth * dpr || canvas.height !== cssHeight * dpr) {
    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, cssWidth, cssHeight);

  const data = peaks.value;
  if (!data.length) return;

  const barCount = data.length;
  const gap = 1;
  const totalGap = gap * (barCount - 1);
  const barWidth = Math.max(1, (cssWidth - totalGap) / barCount);
  const mid = cssHeight / 2;

  const progressRatio =
    duration.value > 0 ? Math.min(1, currentTime.value / duration.value) : 0;
  const playedX = progressRatio * cssWidth;

  const styles = getComputedStyle(canvas);
  const playedColor = styles.getPropertyValue("--audio-played").trim() || "hsl(var(--primary, 222 47% 11%))";
  const trackColor = styles.getPropertyValue("--audio-track").trim() || "hsl(var(--muted-foreground, 215 16% 47%) / 0.45)";

  for (let i = 0; i < barCount; i += 1) {
    const x = i * (barWidth + gap);
    const amplitude = Math.max(0.04, data[i]);
    const half = (amplitude * cssHeight) / 2;
    ctx.fillStyle = x + barWidth <= playedX ? playedColor : trackColor;
    ctx.fillRect(x, mid - half, barWidth, half * 2);
  }
}

async function loadAudio() {
  if (objectUrl.value) return;
  loading.value = true;
  loadError.value = null;
  try {
    const blob = await useApiFetch<Blob>(
      `/v1/file/${props.siteId}/download/${props.file.path}`,
      { responseType: "blob" }
    );
    objectUrl.value = URL.createObjectURL(blob);
    nextTick(() => {
      const el = audioEl.value;
      if (el) {
        el.src = objectUrl.value!;
        el.load();
      }
    });

    try {
      const AC: typeof AudioContext =
        window.AudioContext ?? (window as any).webkitAudioContext;
      if (AC) {
        const arrayBuffer = await blob.arrayBuffer();
        const ctx = new AC();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
        peaks.value = computePeaks(audioBuffer, WAVE_BUCKETS);
        waveformReady.value = true;
        await ctx.close();
        await nextTick();
        drawWaveform();
      }
    } catch (err) {
      // Decoding failures are non-fatal; we keep the audio playable but
      // fall back to a flat progress bar via `waveformReady = false`.
      console.warn("Failed to decode audio waveform", err);
    }
  } catch (err: any) {
    loadError.value = err?.message ?? "Failed to load audio";
  } finally {
    loading.value = false;
  }
}

async function togglePlay() {
  const el = audioEl.value;
  if (!el) return;
  if (!objectUrl.value) {
    await loadAudio();
  }
  if (loadError.value) return;
  if (el.paused) {
    try {
      await el.play();
    } catch (err) {
      console.warn("Audio playback failed", err);
    }
  } else {
    el.pause();
  }
}

function onTimeUpdate() {
  const el = audioEl.value;
  if (!el) return;
  currentTime.value = el.currentTime;
  if (waveformReady.value) drawWaveform();
}

function onLoadedMetadata() {
  const el = audioEl.value;
  if (!el) return;
  if (Number.isFinite(el.duration)) duration.value = el.duration;
}

function onPlay() {
  isPlaying.value = true;
}
function onPause() {
  isPlaying.value = false;
}
function onEnded() {
  isPlaying.value = false;
  const el = audioEl.value;
  if (el) {
    el.currentTime = 0;
    currentTime.value = 0;
    if (waveformReady.value) drawWaveform();
  }
}

async function seekFromEvent(event: MouseEvent | PointerEvent) {
  const el = audioEl.value;
  const canvas = waveformEl.value;
  if (!canvas) return;
  if (!objectUrl.value) {
    await loadAudio();
  }
  if (!el || !duration.value) return;
  const rect = canvas.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
  el.currentTime = ratio * duration.value;
  currentTime.value = el.currentTime;
  if (waveformReady.value) drawWaveform();
}

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  if (typeof ResizeObserver !== "undefined" && containerEl.value) {
    resizeObserver = new ResizeObserver(() => {
      if (waveformReady.value) drawWaveform();
    });
    resizeObserver.observe(containerEl.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  if (audioEl.value) {
    audioEl.value.pause();
    audioEl.value.src = "";
  }
  if (objectUrl.value) {
    URL.revokeObjectURL(objectUrl.value);
    objectUrl.value = null;
  }
});

const displayLabel = computed(() => props.displayName || props.file.name);
</script>

<template>
  <div ref="containerEl" class="flex flex-1 items-center gap-3 min-w-0">
    <Button
      type="button"
      variant="ghost"
      size="icon"
      class="shrink-0"
      :aria-label="isPlaying ? 'Pause' : 'Play'"
      :disabled="!!loadError"
      @click.stop="togglePlay"
    >
      <icon mode="svg" v-if="loading" name="lucide:loader-circle" class="text-xl animate-spin" />
      <icon mode="svg" v-else-if="isPlaying" name="heroicons:pause" class="text-xl text-primary" />
      <icon mode="svg" v-else name="heroicons:play" class="text-xl text-primary" />
    </Button>

    <div class="flex min-w-0 flex-1 flex-col">
      <p class="truncate text-sm font-medium leading-none">{{ displayLabel }}</p>
      <div class="mt-1 flex items-center gap-2">
        <canvas
          ref="waveformEl"
          class="h-9 w-full min-w-0 cursor-pointer"
          @click.stop="seekFromEvent"
        />
        <span class="shrink-0 text-xs tabular-nums text-muted-foreground">
          {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
        </span>
      </div>
      <p v-if="loadError" class="text-xs text-destructive">{{ loadError }}</p>
    </div>

    <audio
      ref="audioEl"
      preload="none"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @play="onPlay"
      @pause="onPause"
      @ended="onEnded"
    />
  </div>
</template>
